import { useSelector } from "react-redux";
import { Slider } from "@mui/material";
import { RootState } from "app/store";
import { useUserConfig } from "app/hook";
import I18n from "common/i18n";
import { ClickEvent } from "common/types";
import { Switch, RadioButton } from "common/elements";
import { localeToHtmlLang } from "common/utils/locale";

const SoundRow = ({
  icon,
  label,
  volume,
  enabled,
  onVolumeChange,
  onToggle,
}: {
  icon: string;
  label: string;
  volume: number;
  enabled: boolean;
  onVolumeChange: (event: Event, value: number | Array<number>) => void;
  onToggle: (e: ClickEvent) => void;
}) => (
  <div className="flex-row-el items-center justify-center gap-4 py-2">
    <div className="flex-col-center w-[40px] h-[40px] rounded-lg bg-blue-50/40 shrink-0">
      <span className="material-symbols-outlined type-body text-blue-200">{icon}</span>
    </div>
    <label className="type-text type-body text-textcolor-black !text-left w-[150px] shrink-0">
      {label}
    </label>
    <div className="w-[180px] shrink-0 pointer-events-auto">
      <Slider
        min={0}
        max={1}
        step={0.01}
        value={volume}
        onChange={onVolumeChange}
        componentsProps={{
          rail: {
            style: { height: 10, backgroundColor: "#DBD8DA", opacity: 1 },
          },
          track: {
            style: { height: 10, backgroundColor: "#94C4F7", borderColor: "#94C4F7" },
          },
          thumb: {
            style: { width: 34, height: 34, backgroundColor: "#94C4F7" },
          },
        }}
      />
    </div>
    <Switch
      color="#B8B1B5"
      background="#DBEBFC"
      thumb="#94C4F7"
      margin={2}
      width={80}
      height={36}
      value={enabled}
      onClick={onToggle}
    />
  </div>
);

export const DashboardQuickSettings = () => {
  const handleChangeUserConfig = useUserConfig();
  const config = useSelector((state: RootState) => state.config.userConfig);

  const handleChangeLocale = (locale: string) => () => {
    handleChangeUserConfig("locale", locale);
    I18n.locale = locale;
    const rootElement = document.documentElement;
    rootElement.className = rootElement.className
      .replace(/\blang-[a-z]{2}(_[a-z]{2})?\b/g, "")
      .trim();
    if (locale && !locale.includes("ja")) {
      rootElement.classList.add(`lang-${locale}`);
    }
    rootElement.lang = localeToHtmlLang(locale);
  };

  const handleSoundVolume = (_event: Event, value: number | Array<number>) =>
    handleChangeUserConfig("sound.volume", value as number);

  const handleBgmVolume = (_event: Event, value: number | Array<number>) =>
    handleChangeUserConfig("bgm.volume", value as number);

  const handleSoundEnable = (e: ClickEvent) => {
    e.stopPropagation();
    handleChangeUserConfig("sound.enable", !config.sound.enable);
  };

  const handleBgmEnable = (e: ClickEvent) => {
    e.stopPropagation();
    handleChangeUserConfig("bgm.enable", !config.bgm.enable);
  };

  const handleShakeBugReport = (e: ClickEvent) => {
    e.stopPropagation();
    handleChangeUserConfig("shake_bug_report", !config.shake_bug_report);
  };

  return (
    <div className="flex-col-el relative rounded-xl bg-white/60 border-2 border-solid border-beige-60 px-4 pt-4 pb-2 w-full">
      <div className="flex-row-el flex-center absolute -top-3 left-4 px-3 py-1 bg-beige-alt rounded-lg">
        <p className="type-text type-sm text-white">
          {I18n.t("MSG_DASHBOARD_QUICK_SETTINGS_TITLE")}
        </p>
      </div>

      {/* 音量設定 */}
      <SoundRow
        icon="volume_up"
        label={I18n.t("MSG_SETTING_SOUND")}
        volume={config.sound.volume}
        enabled={config.sound.enable}
        onVolumeChange={handleSoundVolume}
        onToggle={handleSoundEnable}
      />
      <SoundRow
        icon="music_note"
        label={I18n.t("MSG_SETTING_BGM")}
        volume={config.bgm.volume}
        enabled={config.bgm.enable}
        onVolumeChange={handleBgmVolume}
        onToggle={handleBgmEnable}
      />

      {/* 端末を振ってバグ報告（アプリのみ。ブラウザでは振っても検知できない） */}
      {window.ReactNativeWebView && (
        <div className="flex-row-el items-center justify-center gap-4 py-2">
          <div className="flex-col-center w-[40px] h-[40px] rounded-lg bg-orange-100/20 shrink-0">
            <span className="material-symbols-outlined type-body text-orange-100">bug_report</span>
          </div>
          {/* 音量行のラベル(150) + スライダー(180) + gap(16) 分を占め、スイッチの位置を揃える */}
          <label className="type-text type-body text-textcolor-black !text-left w-[346px] shrink-0">
            {I18n.t("MSG_SETTING_SHAKE_BUG_REPORT")}
          </label>
          <Switch
            color="#B8B1B5"
            background="#DBEBFC"
            thumb="#94C4F7"
            margin={2}
            width={80}
            height={36}
            value={config.shake_bug_report}
            onClick={handleShakeBugReport}
          />
        </div>
      )}

      {/* 言語設定（音量行と同じ横並び構造） */}
      <div className="flex-row-el items-center justify-center gap-4 py-2">
        <div className="flex-col-center w-[40px] h-[40px] rounded-lg bg-green-40/40 shrink-0">
          <span className="material-symbols-outlined type-body text-green-200">translate</span>
        </div>
        <label className="type-text type-body text-textcolor-black !text-left w-[100px] shrink-0">
          {I18n.t("MSG_DASHBOARD_SETTING_LANGUAGE")}
        </label>
        <div className="flex-row-el items-center gap-x-3 flex-1 pointer-events-auto">
          <RadioButton
            size={28}
            fontSize={18}
            value={config.locale === "ja"}
            label="ひらがな"
            onChange={handleChangeLocale("ja")}
          />
          <RadioButton
            size={28}
            fontSize={18}
            value={config.locale === "ja_kanji"}
            label="漢字"
            onChange={handleChangeLocale("ja_kanji")}
          />
          <RadioButton
            size={28}
            fontSize={18}
            value={config.locale === "en"}
            label="English"
            onChange={handleChangeLocale("en")}
          />
          <RadioButton
            size={28}
            fontSize={18}
            value={config.locale === "zh_cn"}
            label="简体中文"
            onChange={handleChangeLocale("zh_cn")}
          />
          <RadioButton
            size={28}
            fontSize={18}
            value={config.locale === "zh_tw"}
            label="繁體中文"
            onChange={handleChangeLocale("zh_tw")}
          />
        </div>
      </div>
    </div>
  );
};
